import { RoleName } from '@/shared/role-name';
import { User } from '@/shared/user';
import { UserStatus } from '@/shared/user-status';
import { State } from '@/stores/users/state';
import { GetterTree } from 'vuex';

export class Getters implements GetterTree<State, any> {
    [key: string]: (state: State, getters: any, rootState: any, rootGetters: any) => any;

    public users = (state: State) => {
        return sortByName(state.users);
    }

    public activeUsers = (state: State) => {
        return sortByName(state.users.filter((user) => !user.isArchived));
    }

    public activeCustomerExperienceUsers = (state: State) => {
        return sortByName(state.users.filter((user) =>
            !user.isArchived &&
            user.roles.some((role) =>
                role.name.toLowerCase() === RoleName.HeadOfCustomerExperienceTeam.toLowerCase() ||
                role.name.toLowerCase() === RoleName.CustomerExperienceExecutiveTeamLeader.toLowerCase() ||
                role.name.toLowerCase() === RoleName.CustomerExperienceExecutive.toLowerCase())));
    }

    public rightLegalGroupUsers = (state: State) => (status: UserStatus): Array<User> => {
        switch (status) {
            case UserStatus.Active:
                return sortByName(state.users.filter((user) => user.partnerPracticeId == null && !user.isArchived));
            case UserStatus.Archived:
                return sortByName(state.users.filter((user) => user.partnerPracticeId == null && user.isArchived));
            default:
                return sortByName(state.users.filter((user) => user.partnerPracticeId == null));
        }
    }

    public partnerPracticeUsers = (state: State) => (partnerPracticeId: string): Array<User> => {
        return sortByName(state.users.filter((user) => user.partnerPracticeId === partnerPracticeId));
    }

    public activePartnerPracticeUsers = (state: State) => (partnerPracticeId: string): Array<User> => {
        return sortByName(state.users.filter((user) =>
            !user.isArchived &&
            user.partnerPracticeId === partnerPracticeId));
    }

    public activeLegalAdvisors = (state: State) => (partnerPracticeId: string): Array<User> => {
        return sortByName(state.users.filter((user) =>
            !user.isArchived &&
            user.partnerPracticeId === partnerPracticeId &&
            user.roles.some((role) => role.name.toLowerCase() === RoleName.LegalAdvisor.toLowerCase())));
    }

    public legalAdvisors = (state: State) => {
        return sortByName(state.legalAdvisorUsers);
    }

    public appointmentBookers = (state: State) => {
        return sortByName(state.appointmentBookerUsers);
    }

    public interactionAuthors = (state: State) => {
        return sortByName(state.interactionAuthorUsers);
    }

    public taskUsers = (state: State) => {
        const assignedUsers = state.assignedTaskUsers;
        const completedUsers = state.completedTaskUsers;

        const users: Array<User> = [...assignedUsers];

        for (const completedUser of completedUsers.filter((user) => users.some((u) => u.id !== user.id))) {
            users.push({ ...completedUser });
        }

        return sortByName(users);
    }

    public assignedTaskUsers = (state: State) => {
        return sortByName(state.assignedTaskUsers);
    }

    public completedTaskUsers = (state: State) => {
        return sortByName(state.completedTaskUsers);
    }

    public accountAuthors = (state: State) => {
        return sortByName(state.accountAuthorUsers);
    }

    public assignedCallUsers = (state: State) => {
        return sortByName(state.assignedCallUsers);
    }

    public accountAccessUsers = (state: State) => {
        return sortByName(state.accountAccessUsers);
    }

    public targetUsers = (state: State) => {
        return sortByName(state.targetUsers);
    }

    public roles = (state: State) => {
        return state.roles;
    }

    public fullName = (state: State) => (userId: string): string => {
        const user = state.users.find((u) => u.id === userId);
        return user == null ? '' : `${user.firstName.trim()} ${user.lastName.trim()}`.trim();
    }
}

function sortByName(users: Array<User>): Array<User> {
    return users.sort((a, b) => {
        const aFullName = `${a.firstName.trim()} ${a.lastName.trim()}`.trim();
        const bFullName = `${b.firstName.trim()} ${b.lastName.trim()}`.trim();

        if (aFullName > bFullName) { return 1; }
        if (aFullName < bFullName) { return -1; }
        return 0;
    });
}
